import { format, parse, isValid } from 'date-fns';
import { Search, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';

/**
 * The workspace's filters: one date range over invoice_date, one search box.
 *
 * There is deliberately NO client picker. A dedicated dropdown for one entity
 * was clunky next to a search box that could answer the same question, so the
 * client moved into the search itself — the server's search matches client
 * name, first name and last name alongside invoice number, job code and job
 * description, which is how the jobs list has always behaved (NEW-236).
 *
 * Fully controlled. In particular the search value is owned by
 * useInvoiceWorkspaceFilters, which debounces it before it reaches the URL —
 * doing that here would make the input lag its own keystrokes.
 */

const DATE_FORMAT = 'yyyy-MM-dd';

/**
 * The minimum comfortable touch target, matching JobListToolbar. Crew and the
 * owner both use these controls on a phone, so this is the real scene rather
 * than a precaution.
 */
const TOUCH_TARGET = 'min-h-[44px]';

/** '2026-01-05' -> Date, without the UTC shift a bare `new Date(s)` applies. */
const parseISODate = (s: string | undefined): Date | undefined => {
  if (!s) return undefined;
  const d = parse(s, DATE_FORMAT, new Date());
  return isValid(d) ? d : undefined;
};

const toISODate = (d: Date | undefined): string | undefined =>
  d ? format(d, DATE_FORMAT) : undefined;

export interface WorkspaceFiltersProps {
  dateFrom?: string;
  dateTo?: string;
  onDateFromChange: (date: string | undefined) => void;
  onDateToChange: (date: string | undefined) => void;
  /** Immediate value — the hook debounces on the way to the URL. */
  search: string;
  onSearchChange: (value: string) => void;
  /**
   * Stage 1's rows are jobs, which have no invoice date. The server ignores the
   * range there, so showing a control that does nothing would be a lie.
   */
  showDateRange?: boolean;
  onClear: () => void;
  activeCount: number;
}

export function WorkspaceFilters({
  dateFrom,
  dateTo,
  onDateFromChange,
  onDateToChange,
  search,
  onSearchChange,
  showDateRange = true,
  onClear,
  activeCount,
}: WorkspaceFiltersProps) {
  return (
    <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:items-center">
      <div className="relative min-w-0 flex-1 sm:max-w-sm">
        <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder="Client, invoice number, job code or description"
          aria-label="Search invoices"
          className={`pl-8 ${TOUCH_TARGET}`}
        />
      </div>

      {showDateRange && (
        // The two pickers must be able to shrink: fixed w-40 each plus the
        // separator overflowed a 390px viewport and clipped "Invoiced to" off
        // the edge entirely.
        <div className="flex min-w-0 items-center gap-2">
          <DatePicker
            value={parseISODate(dateFrom) ?? null}
            onChange={(d) => onDateFromChange(toISODate(d))}
            placeholder="Invoiced from"
            aria-label="Invoiced from"
            className={`w-full min-w-0 flex-1 sm:w-40 sm:flex-none ${TOUCH_TARGET}`}
          />
          <span className="shrink-0 text-sm text-muted-foreground">to</span>
          <DatePicker
            value={parseISODate(dateTo) ?? null}
            onChange={(d) => onDateToChange(toISODate(d))}
            placeholder="Invoiced to"
            aria-label="Invoiced to"
            className={`w-full min-w-0 flex-1 sm:w-40 sm:flex-none ${TOUCH_TARGET}`}
          />
        </div>
      )}

      {activeCount > 0 && (
        <Button variant="ghost" size="sm" onClick={onClear} className="self-start">
          <X className="mr-1 h-4 w-4" />
          Clear filters
        </Button>
      )}
    </div>
  );
}
